<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="page-header tw-mb-6">
          <div>
            <h2 class="page-title">Motor Renewals</h2>
            <p class="page-subtitle">View Upcoming and Lapsed policy renewals.</p>
          </div>
          <div class="stats-chip">
            <span class="stats-chip-label">Total Count</span>

            <span v-if="loading" class="shimmer shimmer-stat-value"></span>

            <span v-else class="stats-chip-value">
              {{ totalCount }}
            </span>
          </div>
        </header>

        <!-- Type Switch + Search -->
        <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-mb-5">
          <div class="type-switch">
            <button
              :class="['type-switch-btn', { 'type-switch-btn--active': renewalType === 'pending' }]"
              @click="setType('pending')"
            >
              Upcoming
            </button>
            <button
              :class="['type-switch-btn', { 'type-switch-btn--active': renewalType === 'lapsed' }]"
              @click="setType('lapsed')"
            >
              Lapsed
            </button>
          </div>
          <div class="search-wrapper tw-w-full sm:tw-max-w-[380px]">
            <i class="ph ph-magnifying-glass search-icon" style="color: #94A3B8; font-size: 20px;"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by client, policy or product..."
              class="search-input"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <i class="ph ph-x" style="color: #94A3B8; font-size: 18px;"></i>
            </button>
          </div>
        </div>

        <div v-if="!loading && renewalType === 'pending' && renewals.length" class="due-filters tw-mb-5">
          <button
            v-for="f in dueFilterOptions"
            :key="f.key"
            type="button"
            :class="['due-filter', `due-filter--${f.key}`, { 'due-filter--active': dueFilter === f.key }]"
            @click="setDueFilter(f.key)"
          >
            {{ f.label }}
            <span class="due-filter-count">{{ f.count }}</span>
          </button>
        </div>

        <!-- Loading Skeleton -->
        <div v-if="loading" class="claims-card">
          <div class="renewal-cards">
            <div v-for="n in 3" :key="`renewal-card-skeleton-${n}`" class="r-card">
              <div class="shimmer shimmer-client"></div>
              <div class="shimmer shimmer-product tw-mt-3"></div>
              <div class="shimmer shimmer-amount tw-mt-3"></div>
            </div>
          </div>
          <div class="table-scroll-wrap renewals-table-wrap">
            <table class="claims-table">
              <thead>
                <tr>
                  <th class="client-col">Client</th>
                  <th class="renewal-col">Renewal</th>
                  <th class="premium-col">Renewal Premium</th>
                  <th class="actions-col">Actions</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="n in 6"
                  :key="`renewal-skeleton-${n}`"
                  class="claims-row skeleton-row"
                >
                  <td>
                    <div class="shimmer shimmer-client"></div>
                  </td>

                  <td>
                    <div class="shimmer shimmer-date"></div>
                  </td>

                  <td>
                    <div class="shimmer shimmer-amount"></div>
                  </td>

                  <td class="actions-col">
                    <div class="tw-flex tw-items-center tw-gap-2">
                      <div class="shimmer shimmer-action"></div>
                      <div class="shimmer shimmer-icon"></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Table Card -->
        <div v-else class="claims-card">
          <div v-if="filteredRenewals.length > 0" class="renewal-cards">
            <div
              v-for="(r, idx) in filteredRenewals"
              :key="`card-${r.policyNo || 'n'}-${idx}`"
              class="r-card"
              @click="openDetailsModal(r)"
            >
              <div class="r-card__top">
                <div class="r-card__who">
                  <p class="r-card__name">{{ r.clientName }}</p>
                  <p class="r-card__meta">{{ vehicleOf(r) || r.product }} · {{ r.policyNo }}</p>
                </div>
                <span :class="['due-chip', `due-chip--${dueOf(r).urgency.key}`]">{{ dueOf(r).urgency.label }}</span>
              </div>
              <div class="r-card__facts">
                <div class="r-card__fact">
                  <span>Renewal date</span>
                  <strong>{{ formatDate(r.renewalDate) }}</strong>
                  <small>{{ dueOf(r).dueText }}</small>
                </div>
                <div class="r-card__fact">
                  <span>Renewal premium</span>
                  <strong>{{ formatCurrency(r.premium) }}</strong>
                  <small>Sum insured {{ formatCurrency(r.sumInsured) }}</small>
                </div>
              </div>
              <div class="r-card__actions">
                <button v-if="canRenew(r)" class="btn-renew" @click.stop="startRenewal(r)">Renew Now</button>
                <span v-else-if="isRenewalClosed(r)" class="renewal-closed">New quote needed</span>
                <button class="btn-view" @click.stop="openDetailsModal(r)">View Details</button>
                <v-btn
                  icon small
                  title="Policy Documents"
                  class="r-card__docs"
                  style="color:#2B6CB0;"
                  @click.stop="openDocsModal(r)"
                >
                  <v-icon small>mdi-folder-multiple-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div v-if="filteredRenewals.length > 0" class="table-scroll-wrap renewals-table-wrap">
            <table class="claims-table">
            <thead>
              <tr>
                <th class="client-col">Client</th>
                <th class="renewal-col">Renewal</th>
                <th class="premium-col">Renewal Premium</th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(r, idx) in filteredRenewals"
                :key="`${r.policyNo || 'n'}-${idx}`"
                class="claims-row"
                @click="openDetailsModal(r)"
              >
                <td class="client-cell">
                  <div class="client-name">{{ r.clientName }}</div>
                  <div class="client-meta">
                    <span v-if="vehicleOf(r)" class="client-reg">{{ vehicleOf(r) }}</span>
                    <span>{{ r.policyNo }}</span>
                  </div>
                </td>
                <td>
                  <div class="renewal-cell">
                    <span class="renewal-date">{{ formatDate(r.renewalDate) }}</span>
                    <span class="due-line">
                      <span :class="['due-chip', `due-chip--${dueOf(r).urgency.key}`]">{{ dueOf(r).urgency.label }}</span>
                      <span class="due-text">{{ dueOf(r).dueText }}</span>
                    </span>
                  </div>
                </td>
                <td>
                  <div class="cell-text cell-strong">{{ formatCurrency(r.premium) }}</div>
                  <div class="cell-sub">Sum insured {{ formatCurrency(r.sumInsured) }}</div>
                </td>
                <td class="actions-col" @click.stop>
                  <div class="actions-cell">
                  <button
                    v-if="canRenew(r)"
                    class="btn-renew"
                    @click.stop="startRenewal(r)"
                  >
                    <v-icon small color="white">mdi-autorenew</v-icon>
                    Renew Now
                  </button>
                  <span
                    v-else-if="isRenewalClosed(r)"
                    class="renewal-closed"
                    title="Cover ended more than 2 months ago. Start a new quote under Purchase Cover."
                  >
                    New quote needed
                  </span>
                  <v-btn
                    icon small
                    title="View details"
                    aria-label="View details"
                    style="color:#2B6CB0;"
                    @click.stop="openDetailsModal(r)"
                  >
                    <v-icon small>mdi-eye-outline</v-icon>
                  </v-btn>
                  <v-btn
                    icon small
                    title="Policy documents"
                    aria-label="Policy documents"
                    style="color:#2B6CB0;"
                    @click.stop="openDocsModal(r)"
                  >
                    <v-icon small>mdi-folder-multiple-outline</v-icon>
                  </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <i class="ph ph-calendar-check" style="font-size: 60px; color: #CBD5E1;"></i>
            <p class="empty-title">No {{ renewalType === 'pending' ? 'upcoming' : 'lapsed' }} renewals</p>
            <p class="empty-subtitle">Try switching type, clearing the due filter or adjusting your search.</p>
          </div>
        </div>

      </div>
    </div>

    <!-- Renewal Details Modal -->
    <v-dialog v-model="detailsModalVisible" max-width="480" persistent content-class="conversion-modal">
      <!-- ... (keep existing detail modal contents for fallback) ... -->
      <v-card v-if="selectedRenewal" class="conversion-modal-card">
        <div class="conversion-modal-header">
          <h3 class="conversion-modal-title">Renewal Details</h3>
          <v-btn icon small @click="closeDetailsModal" class="conversion-modal-close">
            <i class="ph ph-x"></i>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-card-text class="conversion-modal-body">
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Client</span>
            <span class="conversion-detail-value">{{ selectedRenewal.clientName }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Product</span>
            <span class="conversion-detail-value">
              <span class="badge badge-info">{{ selectedRenewal.product }}</span>
            </span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Policy No</span>
            <span class="conversion-detail-value">{{ selectedRenewal.policyNo || '—' }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Renewal Date</span>
            <span class="conversion-detail-value">{{ formatDate(selectedRenewal.renewalDate) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Premium</span>
            <span class="conversion-detail-value">{{ formatCurrency(selectedRenewal.premium) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Sum Insured</span>
            <span class="conversion-detail-value">{{ formatCurrency(selectedRenewal.sumInsured) }}</span>
          </div>
          <div class="conversion-detail-row" v-if="selectedRenewal.yourRef">
            <span class="conversion-detail-label">Your Ref</span>
            <span class="conversion-detail-value">{{ selectedRenewal.yourRef }}</span>
          </div>
          <div class="conversion-detail-row" v-if="selectedRenewal.effectiveBatchNo">
            <span class="conversion-detail-label">Effective Batch No</span>
            <span class="conversion-detail-value">{{ selectedRenewal.effectiveBatchNo }}</span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="conversion-modal-actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeDetailsModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Documents Modal -->
    <v-dialog v-model="docsModalVisible" max-width="680" content-class="docs-modal">
      <v-card class="docs-modal-card">
        <div class="docs-modal-header">
          <div>
            <h3 class="docs-modal-title">Policy Documents</h3>
            <p class="docs-modal-subtitle">{{ docsModalTitle }}</p>
          </div>
          <v-btn icon small @click="docsModalVisible = false">
            <v-icon size="20" color="#718096">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>

        <div v-if="docsModalLoading" class="docs-modal-loader">
          <v-progress-circular indeterminate color="#00AEEF" size="32"></v-progress-circular>
          <span style="color:#94A3B8; font-size:0.85rem; margin-top:12px;">Loading documents...</span>
        </div>
        <div v-else-if="docsModalError" class="docs-modal-empty">
          <v-icon size="40" color="#FC8181">mdi-alert-circle-outline</v-icon>
          <p style="color:#E53E3E; font-size:0.9rem; font-weight:500; margin-top:8px;">{{ docsModalError }}</p>
        </div>
        <div v-else-if="docsModalList.length === 0" class="docs-modal-empty">
          <v-icon size="48" color="#CBD5E1">mdi-folder-open-outline</v-icon>
          <p style="color:#94A3B8; font-size:0.95rem; margin-top:8px; font-weight:500;">
            No documents found for this policy.
          </p>
        </div>
        <div v-else class="docs-modal-list">
          <div v-for="doc in docsModalList" :key="doc.id" class="doc-list-item">
            <div class="doc-list-icon">
              <v-icon size="24" :color="doc.mimeType === '.pdf' ? '#E53E3E' : '#718096'">
                {{ doc.mimeType === '.pdf' ? 'mdi-file-pdf-box' : 'mdi-file-document-outline' }}
              </v-icon>
            </div>
            <div class="doc-list-info">
              <p class="doc-list-name" :title="doc.actualName">{{ doc.actualName }}</p>
              <div class="doc-list-meta">
                <span class="doc-type-badge">{{ doc.docType }}</span>
                <span class="doc-date">{{ doc.dateCreated }}</span>
              </div>
            </div>
            <div class="doc-view-btn">
              <v-btn small depressed color="primary" @click="openDocument(doc)">
                <v-icon x-small left>mdi-eye-outline</v-icon>
                View
              </v-btn>
            </div>
          </div>
        </div>

        <v-divider></v-divider>
        <v-card-actions class="tw-py-3 tw-px-5">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="docsModalVisible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Document Viewer Modal -->
    <v-dialog
      v-model="docViewerVisible"
      max-width="1100"
      max-height="90vh"
      content-class="doc-viewer-dialog"
      @click:outside="closeDocViewer"
    >
      <v-card class="doc-viewer-card">
        <!-- Header -->
        <div class="doc-viewer-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <v-icon color="#E53E3E" size="22">mdi-file-pdf-box</v-icon>
            <div>
              <h3 class="doc-viewer-title">{{ docViewerTitle }}</h3>
              <p class="doc-viewer-subtitle">Document Preview</p>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <v-btn
              v-if="docViewerUrl"
              small outlined color="primary"
              @click="openDocFull"
            >
              <v-icon x-small left>mdi-open-in-new</v-icon>
              Open Full
            </v-btn>
            <v-btn icon small @click="closeDocViewer">
              <v-icon size="20" color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>

        <!-- Loading state -->
        <div v-if="docViewerLoading" class="doc-viewer-loading">
          <v-progress-circular indeterminate color="#00AEEF" size="48"></v-progress-circular>
          <p class="tw-mt-4" style="color:#94A3B8; font-size:0.9rem;">Loading document…</p>
        </div>

        <!-- IFrame Content -->
        <div v-else-if="docViewerUrl" class="doc-viewer-frame-wrap">
          <iframe
            :src="docViewerUrl"
            class="doc-viewer-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </v-card>
    </v-dialog>
  </agent-default>
</template>

<script>
import axios from 'axios';
import AgentDefault from '@/components/layout/AgentDefault';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import {
  canRenewNow,
  isLapsedBeyondRenewal,
  isMotorRenewal,
  saveRenewalToSession,
  mergeGisRenewalBatchDetail,
  policyToSlug,
} from '@/utils/agentRenewal';
import { fetchGisRenewalBatchDetail } from '@/services/renewalApi';
import {
  CLOSED_URGENCY,
  REMINDER_ATTENTION_DAYS,
  REMINDER_URGENT_DAYS,
  daysUntil,
  dueText,
  parseRenewalDate,
  urgencyOf,
  vehicleLabel,
} from '@/utils/renewalReminders';

const DUE_FILTERS = {
  all: () => true,
  week: (days) => days != null && days <= REMINDER_URGENT_DAYS,
  month: (days) => days != null && days > REMINDER_URGENT_DAYS && days <= REMINDER_ATTENTION_DAYS,
  later: (days) => days == null || days > REMINDER_ATTENTION_DAYS,
};

export default {
  name: 'AgentRenewals',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      searchQuery: '',
      renewalType: 'pending',
      dueFilter: 'all',
      renewals: [],
      /** Full GIS match count before motor filter (from totalFilteredRecords). */
      totalFilteredRecords: 0,
      detailsModalVisible: false,
      selectedRenewal: null,

      docsModalVisible: false,
      docsModalLoading: false,
      docsModalTitle: '',
      docsModalList: [],
      docsModalError: null,

      docViewerVisible: false,
      docViewerUrl: '',
      docViewerTitle: '',
      docViewerLoading: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
    totalCount() {
      // Full GIS pages are loaded first; this is the motor list (and search subset).
      return this.filteredRenewals.length;
    },
    dueFilterOptions() {
      const count = (key) => this.renewals.filter((r) => DUE_FILTERS[key](daysUntil(r.renewalDate))).length;
      return [
        { key: 'all', label: 'All upcoming', count: this.renewals.length },
        { key: 'week', label: 'Within 7 days', count: count('week') },
        { key: 'month', label: 'In 8 to 30 days', count: count('month') },
        { key: 'later', label: 'Later', count: count('later') },
      ];
    },
    filteredRenewals() {
      const query = (this.searchQuery || '').trim().toLowerCase();
      const matchesDue = this.renewalType === 'pending' ? DUE_FILTERS[this.dueFilter] : DUE_FILTERS.all;
      const rows = this.renewals.filter((r) => {
        if (!matchesDue(daysUntil(r.renewalDate))) return false;
        if (!query) return true;
        const name = (r.clientName || '').toLowerCase();
        const policyNo = (r.policyNo || '').toLowerCase();
        const product = (r.product || '').toLowerCase();
        return name.includes(query) || policyNo.includes(query) || product.includes(query);
      });
      const time = (r) => parseRenewalDate(r.renewalDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const direction = this.renewalType === 'lapsed' ? -1 : 1;
      return rows.sort((a, b) => direction * (time(a) - time(b)));
    },
  },
  watch: {
    renewalType() {
      this.dueFilter = 'all';
      this.fetchRenewals();
    },
  },
  mounted() {
    this.fetchRenewals();
  },
  methods: {
    setType(type) {
      if (type === 'pending' || type === 'lapsed') {
        this.renewalType = type;
        trackAgentInteraction(this, 'renewals_toggle_type', { renewal_type: type });
      }
    },
    buildRenewalsUrl({
      agnCode,
      email,
      pageNumber,
      limit,
    }) {
      const baseUrl = '/customer/gis/agent/renewals';
      const params = new URLSearchParams({
        type: this.renewalType,
        pageNumber: String(pageNumber),
        limit: String(limit),
      });
      if (agnCode) {
        params.set('agn_code', String(agnCode));
        params.set('agnCode', String(agnCode));
      } else if (email) {
        params.set('email', email);
      }
      return `${baseUrl}?${params.toString()}`;
    },
    mapRenewalRow(r) {
      return {
        ...r,
        premium: r.premium ?? r.futurePremium ?? 0,
        sumInsured: r.sumInsured ?? 0,
      };
    },
    extractRenewalPage(res) {
      if (!res || res.status !== 200 || !res.data) {
        return { pageItems: [], total: null };
      }
      const pageObj = res.data?.object ?? res.data;
      const raw = pageObj?.pageItems
        ?? res.data?.renewals
        ?? pageObj?.renewals
        ?? res.data?.data;
      const pageItems = Array.isArray(raw) ? raw : [];
      const reportedTotal = Number(
        pageObj?.totalFilteredRecords
          ?? res.data?.totalFilteredRecords
          ?? pageObj?.totalRecords,
      );
      const total = Number.isFinite(reportedTotal) && reportedTotal >= 0
        ? reportedTotal
        : null;
      return { pageItems, total };
    },
    /**
     * GIS defaults to pageSize 30; pull every page so client search covers the full set.
     * API hard-caps limit at 200.
     */
    async fetchRenewals() {
      const agnCode = this.authenticatedUser?.['custom:agent_code'];
      const email = this.authenticatedUser?.email;
      if (!agnCode && !email) {
        this.loading = false;
        this.renewals = [];
        this.totalFilteredRecords = 0;
        return;
      }
      this.loading = true;
      try {
        const pageSize = 200;
        const maxPages = 50;
        const firstRes = await axios.get(
          this.buildRenewalsUrl({
            agnCode,
            email,
            pageNumber: 1,
            limit: pageSize,
          }),
          { validateStatus: () => true },
        );
        const first = this.extractRenewalPage(firstRes);
        if (firstRes.status !== 200 || !firstRes.data) {
          this.renewals = [];
          this.totalFilteredRecords = 0;
          return;
        }

        let { total } = first;
        const allItems = [...first.pageItems];

        const knownTotal = total != null ? total : allItems.length;
        const remainingPages = Math.min(
          maxPages,
          Math.max(0, Math.ceil(knownTotal / pageSize) - 1),
        );

        // If total was missing but first page was full, probe page 2+ sequentially via Promise chain
        // only when we know more pages exist from totalFilteredRecords.
        if (remainingPages > 0) {
          const pageNumbers = Array.from({ length: remainingPages }, (_, i) => i + 2);
          const restResponses = await Promise.all(
            pageNumbers.map((pageNumber) => axios.get(
              this.buildRenewalsUrl({
                agnCode,
                email,
                pageNumber,
                limit: pageSize,
              }),
              { validateStatus: () => true },
            )),
          );
          restResponses.forEach((res) => {
            const { pageItems, total: pageTotal } = this.extractRenewalPage(res);
            allItems.push(...pageItems);
            if (pageTotal != null) total = pageTotal;
          });
        }

        this.totalFilteredRecords = total != null ? total : allItems.length;
        this.renewals = allItems.map(this.mapRenewalRow).filter(isMotorRenewal);
        if (this.renewalType === 'pending') {
          this.$store.dispatch('renewalReminders/sync', this.renewals);
        }
      } catch {
        this.renewals = [];
        this.totalFilteredRecords = 0;
      } finally {
        this.loading = false;
      }
    },
    canRenew(r) {
      return canRenewNow(r, this.renewalType);
    },
    setDueFilter(key) {
      this.dueFilter = key;
      trackAgentInteraction(this, 'renewals_due_filter', { due_filter: key });
    },
    vehicleOf(r) {
      return vehicleLabel(r);
    },
    isRenewalClosed(r) {
      return isLapsedBeyondRenewal(r);
    },
    dueOf(r) {
      const days = daysUntil(r.renewalDate);
      return {
        urgency: this.isRenewalClosed(r) ? CLOSED_URGENCY : urgencyOf(days),
        dueText: dueText(days),
      };
    },
    persistRenewal(r) {
      saveRenewalToSession(r, this.renewalType);
    },
    async startRenewal(r) {
      if (!this.canRenew(r) || !r?.policyNo) return;
      if (!isMotorRenewal(r)) {
        this.$notify.error('Only motor policies can be renewed in the portal.');
        return;
      }
      let renewal = r;
      const batchNo = r.effectiveBatchNo || r.batchNo;
      if (batchNo) {
        const detailRes = await fetchGisRenewalBatchDetail(batchNo);
        if (detailRes.success && detailRes.data) {
          renewal = mergeGisRenewalBatchDetail(r, detailRes.data);
          if (!isMotorRenewal(renewal)) {
            this.$notify.error('Only motor policies can be renewed in the portal.');
            return;
          }
        } else if (detailRes.message) {
          this.$notify.error(detailRes.message || 'Could not refresh renewal details from GIS.');
          return;
        }
      }
      const slug = policyToSlug(renewal.policyNo);
      this.persistRenewal(renewal);
      trackAgentInteraction(this, 'renewal_renew_now_click', {
        policy_number: renewal.policyNo,
        renewal_type: this.renewalType,
        source: 'renewals_list',
      });
      this.$router.push({ name: 'AgentRenewPolicy', params: { policyNo: slug } });
    },
    openDetailsModal(r) {
      trackAgentInteraction(this, 'renewals_open_details', {
        policy_number: r?.policyNo || 'unknown',
      });
      if (r.policyNo) {
        const slug = policyToSlug(r.policyNo);
        this.persistRenewal(r);
        this.$router.push({ name: 'AgentRenewalDetails', params: { policyNo: slug } });
      } else {
        this.selectedRenewal = r;
        this.detailsModalVisible = true;
      }
    },
    closeDetailsModal() {
      this.detailsModalVisible = false;
      this.selectedRenewal = null;
    },
    async openDocsModal(ren) {
      trackAgentInteraction(this, 'renewals_open_documents', {
        policy_number: ren?.policyNo || 'unknown',
      });
      const { policyNo } = ren;
      if (!policyNo) return;

      this.docsModalTitle = `Documents — Policy ${policyNo}`;
      this.docsModalList = [];
      this.docsModalError = null;
      this.docsModalVisible = true;
      this.docsModalLoading = true;

      try {
        const res = await axios.get(
          `/customer/document-api/v1/getNBDocByPolicyNo?policyNo=${policyNo}`,
          { validateStatus: () => true },
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          const ALLOWED = ['.pdf', '.doc', '.docx'];
          this.docsModalList = res.data.filter(
            (doc) => ALLOWED.includes((doc.mimeType || '').toLowerCase()),
          );
        } else {
          this.docsModalError = 'Could not load documents for this policy.';
        }
      } catch {
        this.docsModalError = 'An error occurred while fetching documents.';
      } finally {
        this.docsModalLoading = false;
      }
    },
    openDocument(doc) {
      const docId = typeof doc === 'object' ? doc.id : doc;
      const docName = typeof doc === 'object' ? (doc.actualName || `Document #${docId}`) : `Document #${docId}`;
      trackAgentInteraction(this, 'renewals_open_document', {
        document_id: docId,
        document_name: docName,
      });

      this.docViewerTitle = docName;
      this.docViewerLoading = false;
      const basePath = process.env.NODE_ENV === 'production' ? '/api' : '';
      this.docViewerUrl = `${basePath}/customer/document-api/v1/viewUWDocs/${docId}`;
      this.docViewerVisible = true;
    },
    closeDocViewer() {
      this.docViewerVisible = false;
      this.docViewerUrl = '';
    },
    openDocFull() {
      if (this.docViewerUrl) {
        trackAgentInteraction(this, 'renewals_open_document_full', {
          document_name: this.docViewerTitle || 'unknown',
        });
        window.open(this.docViewerUrl, '_blank', 'noopener,noreferrer');
      }
    },
    formatDate(val) {
      if (!val) return '—';
      try {
        return new Date(val).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return val;
      }
    },
    formatCurrency(value) {
      if (value == null) return '';
      return `Ksh ${Number(value).toLocaleString()}`;
    },
  },
  metaInfo: { title: 'Renewals' },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* ── Stats Chip ── */
.stats-chip {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  border-radius: 14px;
  padding: 14px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 110px;
  box-shadow: 0 4px 12px rgba(30, 66, 118, 0.2);
}

.stats-chip-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stats-chip-value {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  line-height: 1.1;
  margin-top: 2px;
}

/* ── Type Switch ── */
.due-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.due-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #E2E8F0;
  background: #fff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.due-filter:hover {
  border-color: #CBD5E1;
}

.due-filter--active {
  background: #1E4276;
  border-color: #1E4276;
  color: #fff;
}

.due-filter-count {
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #EEF2F6;
  color: #1E4276;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 18px;
  text-align: center;
}

.due-filter--week .due-filter-count { background: #FEEBC8; color: #9C4221; }
.due-filter--active .due-filter-count { background: rgba(255, 255, 255, 0.2); color: #fff; }

.due-chip {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  white-space: nowrap;
}

.due-chip--ended { background: #FED7D7; color: #9B2C2C; }
.due-chip--now { background: #FEEBC8; color: #9C4221; }
.due-chip--soon { background: #E0F2FE; color: #075985; }
.due-chip--upcoming,
.due-chip--ready { background: #E8EEF7; color: #1E4276; }
.due-chip--closed { background: #F1F5F9; color: #64748B; }

.renewal-closed {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748B;
  white-space: nowrap;
  cursor: help;
}

.due-text {
  font-size: 0.75rem;
  color: #64748B;
  white-space: nowrap;
}

.type-switch {
  display: inline-flex;
  background: #F1F5F9;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
}

.type-switch-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748B;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.type-switch-btn:hover {
  color: #1E4276;
}

.type-switch-btn--active {
  background: white;
  color: #00AEEF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ── Search ── */
.search-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 0 14px;
  height: 48px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-wrapper:focus-within {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.search-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9rem;
  color: #1E4276;
  background: transparent;
}

.search-input::placeholder {
  color: #94A3B8;
}

.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
}

/* ── Claims Card ── */
.claims-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* ── Mobile cards (table below 768px is unreadable) ── */
.renewal-cards {
  display: none;
}

@media (max-width: 767px) {
  .renewals-table-wrap {
    display: none;
  }

  .renewal-cards {
    display: flex;
    flex-direction: column;
  }
}

.r-card {
  padding: 14px 16px;
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
}

.r-card:last-child {
  border-bottom: 0;
}

.r-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.r-card__who {
  min-width: 0;
}

.r-card__name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
}

.r-card__meta {
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: #64748B;
  word-break: break-word;
}

.r-card__facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 12px 0;
  padding: 10px 12px;
  background: #F8FAFC;
  border-radius: 10px;
}

.r-card__fact {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.r-card__fact span {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #94A3B8;
}

.r-card__fact strong {
  font-size: 0.9rem;
  color: #1E4276;
}

.r-card__fact small {
  font-size: 0.72rem;
  color: #64748B;
}

.r-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.r-card__actions .btn-renew,
.r-card__actions .btn-view {
  flex: 1;
  padding: 9px 12px;
  justify-content: center;
  text-align: center;
}

.r-card__docs {
  flex-shrink: 0;
}

@media (max-width: 767px) {
  .page-header {
    align-items: center;
  }

  .page-title {
    font-size: 1.2rem;
  }

  .stats-chip {
    min-width: 0;
    padding: 8px 14px;
  }

  .stats-chip-label {
    font-size: 0.62rem;
  }

  .stats-chip-value {
    font-size: 1.35rem;
  }

  .type-switch {
    width: 100%;
  }

  .type-switch-btn {
    flex: 1;
  }
}

/* ── Table ── */
.table-scroll-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 1px;
}

.claims-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
}

@media (max-width: 640px) {
  .claims-table th,
  .claims-table td {
    padding: 12px 14px;
    font-size: 0.85rem;
  }
}

.claims-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.claims-table th {
  text-align: left;
  padding: 14px 20px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.claims-row {
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
  transition: background 0.15s;
}

.claims-row:last-child {
  border-bottom: none;
}

.claims-row:hover {
  background: #F0F9FF;
}

.claims-table td {
  padding: 16px 20px;
  vertical-align: middle;
}

.client-name {
  font-weight: 700;
  color: #1E4276;
  font-size: 0.95rem;
}

.cell-text {
  font-size: 0.9rem;
  color: #334155;
}

.cell-muted {
  color: #94A3B8;
  font-size: 0.875rem;
}

.client-cell {
  min-width: 200px;
}

.client-col {
  width: 38%;
}

.renewal-col {
  width: 22%;
}

.premium-col {
  width: 24%;
}

.client-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin-top: 2px;
  font-size: 0.75rem;
  color: #64748B;
}

.client-reg {
  font-weight: 700;
  color: #334155;
}

.renewal-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.renewal-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}

.due-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}

.cell-strong {
  font-weight: 700;
  color: #1E4276;
  white-space: nowrap;
}

.cell-sub {
  margin-top: 2px;
  font-size: 0.75rem;
  color: #94A3B8;
  white-space: nowrap;
}

/* ── Action Buttons ── */
.actions-col {
  position: sticky;
  right: 0;
  width: 1%;
  background: #fff;
  box-shadow: -8px 0 12px -10px rgba(15, 23, 42, 0.18);
}

.claims-table thead .actions-col {
  background: #F8FAFC;
}

.claims-row:hover .actions-col {
  background: #F0F9FF;
}

.actions-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.btn-renew {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #00AEEF;
  border: none;
  color: white;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}

.btn-renew:hover {
  background: #0099d4;
  box-shadow: 0 2px 8px rgba(0, 174, 239, 0.35);
}

.btn-view {
  background: transparent;
  border: 1.5px solid #00AEEF;
  color: #00AEEF;
  padding: 6px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}

.btn-view:hover {
  background: #00AEEF;
  color: white;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
/* ── Shimmer Loading ── */

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: renewal-shimmer 1.5s infinite;
}

@keyframes renewal-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* Total count number */
.shimmer-stat-value {
  width: 64px;
  height: 34px;
  margin-top: 4px;
  border-radius: 6px;
}

/* Table skeletons */
.shimmer-client {
  width: 150px;
  height: 18px;
}

.shimmer-product {
  width: 120px;
  height: 30px;
  border-radius: 20px;
}

.shimmer-date {
  width: 90px;
  height: 17px;
}

.shimmer-amount {
  width: 100px;
  height: 17px;
}

.shimmer-action {
  width: 100px;
  height: 32px;
  border-radius: 8px;
}

.shimmer-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.skeleton-row {
  cursor: default !important;
}

.skeleton-row:hover {
  background: transparent !important;
}
</style>

<style>
.conversion-modal .v-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.conversion-modal-card {
  border-radius: 12px;
}

.conversion-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}

.conversion-modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #184679;
  margin: 0;
}

.conversion-modal-close {
  color: #718096;
}

.conversion-modal-body {
  padding: 24px !important;
}

.conversion-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #E2E8F2;
  gap: 16px;
}

.conversion-detail-row:last-child {
  border-bottom: none;
}

.conversion-detail-label {
  font-size: 0.85rem;
  color: #718096;
  font-weight: 500;
  min-width: 120px;
}

.conversion-detail-value {
  font-size: 0.95rem;
  color: #2D3748;
  text-align: right;
}

.conversion-modal-actions {
  padding: 16px 24px !important;
}

.badge-info {
  background: #EBF8FF;
  color: #2B6CB0;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.85rem;
}

.docs-modal-card { border-radius: 14px !important; }
.docs-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
}
.docs-modal-title { font-size: 1.05rem; font-weight: 700; color: #184679; margin: 0; }
.docs-modal-subtitle { font-size: 0.78rem; color: #94A3B8; margin: 2px 0 0; }
.docs-modal-loader, .docs-modal-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 20px;
}
.docs-modal-list { max-height: 400px; overflow-y: auto; padding: 8px 0; }
.doc-list-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #F1F5F9;
  transition: background 0.15s;
}
.doc-list-item:hover { background: #F8FAFC; }
.doc-list-item:last-child { border-bottom: none; }
.doc-list-icon { width: 32px; display: flex; justify-content: center; flex-shrink: 0; }
.doc-list-info { flex: 1; min-width: 0; }
.doc-list-name {
  font-size: 0.875rem; font-weight: 600; color: #2D3748;
  margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.doc-list-meta { display: flex; align-items: center; gap: 8px; margin-top: 3px; }
.doc-type-badge {
  font-size: 0.7rem; font-weight: 700;
  background: #EBF8FF; color: #2B6CB0;
  padding: 1px 7px; border-radius: 10px;
}
.doc-date { font-size: 0.75rem; color: #94A3B8; }
.doc-view-btn { flex-shrink: 0; }

.doc-viewer-card {
  border-radius: 14px !important;
  display: flex;
  flex-direction: column;
  /* Use viewport height so it's readable */
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
}
.doc-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #FAFBFC;
  flex-shrink: 0;
}
.doc-viewer-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0;
}
.doc-viewer-subtitle {
  font-size: 0.72rem;
  color: #94A3B8;
  margin: 0;
}
.doc-viewer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
}
.doc-viewer-frame-wrap {
  flex: 1;
  overflow: hidden;
  display: flex;
}
.doc-viewer-iframe {
  width: 100%;
  /* Fill the full card height */
  height: 100%;
  min-height: 600px;
  border: none;
  display: block;
  flex: 1;
}
.doc-viewer-dialog {
  border-radius: 14px !important;
  overflow: hidden;
  /* Make it tall enough to read without opening full */
  margin: 24px auto !important;
}
</style>
